/**
 * MobileMarkerList Component
 * Optimized marker list for mobile bottom sheet
 * Features:
 * - Horizontal scrolling quick filters
 * - Card-style marker items with smooth animations
 * - Quick stats summary
 * - Search with instant feedback
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import {
  HiOutlineSearch,
  HiOutlineLocationMarker,
  HiOutlineOfficeBuilding,
  HiOutlineHome,
  HiOutlineX,
  HiOutlineEye,
  HiOutlineChevronRight,
  HiOutlineFilter,
  HiOutlineSortDescending,
} from 'react-icons/hi'
import clsx from 'clsx'
import marketMapService from '../../services/marketMapService'

// Quick filter presets
const QUICK_FILTERS = [
  { id: 'all', label: 'Tất cả', icon: HiOutlineLocationMarker },
  { id: 'under2b', label: 'Dưới 2 tỷ', filter: (m) => m.price_value < 2000000000 },
  { id: '2to5b', label: '2-5 tỷ', filter: (m) => m.price_value >= 2000000000 && m.price_value < 5000000000 },
  { id: 'above5b', label: 'Trên 5 tỷ', filter: (m) => m.price_value >= 5000000000 },
]

// Sort options
const SORT_OPTIONS = [
  { id: 'default', label: 'Mặc định' },
  { id: 'price_asc', label: 'Giá thấp → cao' },
  { id: 'price_desc', label: 'Giá cao → thấp' },
  { id: 'area_asc', label: 'Diện tích nhỏ → lớn' },
  { id: 'area_desc', label: 'Diện tích lớn → nhỏ' },
]

function MobileMarkerList({
  markers = [],
  selectedMarker,
  onMarkerClick,
  onViewDetail,
  mapInfo,
  loading,
  availableMaps = [],
  activeMapId,
  onMapChange,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [sortBy, setSortBy] = useState('default')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const searchInputRef = useRef(null)
  const listRef = useRef(null)

  // Filter and sort markers
  const filteredMarkers = useMemo(() => {
    let result = [...markers]

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (m) =>
          m.name?.toLowerCase().includes(query) ||
          m.address?.toLowerCase().includes(query)
      )
    }

    // Quick filter
    if (activeFilter !== 'all') {
      const filterConfig = QUICK_FILTERS.find((f) => f.id === activeFilter)
      if (filterConfig?.filter) {
        result = result.filter(filterConfig.filter)
      }
    }

    // Sort
    if (sortBy !== 'default') {
      result.sort((a, b) => {
        switch (sortBy) {
          case 'price_asc':
            return (a.price_value || 0) - (b.price_value || 0)
          case 'price_desc':
            return (b.price_value || 0) - (a.price_value || 0)
          case 'area_asc':
            return (a.area_m2 || 0) - (b.area_m2 || 0)
          case 'area_desc':
            return (b.area_m2 || 0) - (a.area_m2 || 0)
          default:
            return 0
        }
      })
    }

    return result
  }, [markers, searchQuery, activeFilter, sortBy])

  // Stats
  const stats = useMemo(() => {
    if (!markers.length) return null
    const prices = markers.filter((m) => m.price_value).map((m) => m.price_value)
    return {
      count: markers.length,
      filtered: filteredMarkers.length,
      minPrice: prices.length ? Math.min(...prices) : 0,
      maxPrice: prices.length ? Math.max(...prices) : 0,
    }
  }, [markers, filteredMarkers])

  // Scroll to selected marker
  useEffect(() => {
    if (selectedMarker && listRef.current) {
      const element = listRef.current.querySelector(`[data-marker-id="${selectedMarker.id}"]`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [selectedMarker])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    searchInputRef.current?.focus()
  }, [])

  const handleMarkerClick = useCallback((marker) => {
    onMarkerClick?.(marker)
  }, [onMarkerClick])

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white min-h-full">
        {/* Search skeleton */}
        <div className="p-4 border-b border-gray-100">
          <div className="h-10 bg-gray-100 rounded-xl animate-pulse" />
        </div>
        {/* List skeleton */}
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-20 h-20 bg-gray-100 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
                <div className="h-4 bg-gray-100 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white min-h-full flex flex-col">
      {/* Map selector - horizontal scroll */}
      {availableMaps.length > 1 && (
        <div className="border-b border-gray-100">
          <div className="flex overflow-x-auto scrollbar-hide px-3 py-2 gap-2">
            {availableMaps.map((map) => (
              <button
                key={map.id}
                onClick={() => onMapChange?.(map.id)}
                className={clsx(
                  'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all',
                  activeMapId === map.id
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {map.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/50 relative">
        <div className="relative">
          <HiOutlineSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tìm kiếm địa điểm..."
            className={clsx(
              'w-full bg-white text-gray-900 placeholder-gray-400',
              'rounded-xl pl-12 pr-24 py-3 text-base',
              'border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20',
              'transition-all duration-200'
            )}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-12 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600"
            >
              <HiOutlineX className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className={clsx(
              'absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors',
              sortBy !== 'default'
                ? 'bg-emerald-100 text-emerald-600'
                : 'text-gray-400 hover:bg-gray-100'
            )}
          >
            <HiOutlineSortDescending className="w-5 h-5" />
          </button>
        </div>

        {/* Sort dropdown */}
        {showSortMenu && (
          <div className="absolute right-4 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 z-20 overflow-hidden min-w-[180px] max-w-[calc(100vw-2rem)]">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setSortBy(option.id)
                  setShowSortMenu(false)
                }}
                className={clsx(
                  'w-full px-4 py-3 text-left text-sm transition-colors flex items-center gap-2',
                  sortBy === option.id
                    ? 'bg-emerald-50 text-emerald-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
              >
                {option.label}
                {sortBy === option.id && (
                  <span className="ml-auto text-emerald-500">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick filters - horizontal scroll */}
      <div className="border-b border-gray-100">
        <div className="flex overflow-x-auto scrollbar-hide px-3 py-2 gap-2">
          {QUICK_FILTERS.map((filter) => {
            const Icon = filter.icon || HiOutlineFilter
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={clsx(
                  'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all',
                  activeFilter === filter.id
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {filter.icon && <Icon className="w-4 h-4" />}
                {filter.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{stats.filtered}</span>
            {stats.filtered !== stats.count && (
              <span className="text-gray-400">/{stats.count}</span>
            )}
            {' '}địa điểm
          </span>
          <span className="text-xs text-gray-500">
            {marketMapService.formatPrice(stats.minPrice)} - {marketMapService.formatPrice(stats.maxPrice)}
          </span>
        </div>
      )}

      {/* Marker list */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {filteredMarkers.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredMarkers.map((marker, index) => (
              <div
                key={marker.id}
                data-marker-id={marker.id}
                onClick={() => handleMarkerClick(marker)}
                className={clsx(
                  'flex gap-3 p-4 cursor-pointer transition-all duration-200',
                  'active:bg-gray-50',
                  selectedMarker?.id === marker.id && 'bg-emerald-50 border-l-4 border-emerald-500'
                )}
                style={{
                  animation: `fadeSlideIn 0.3s ease-out ${index * 0.05}s both`,
                }}
              >
                {/* Thumbnail */}
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 shadow-sm">
                  {marker.image_url ? (
                    <img
                      src={marker.image_url}
                      alt={marker.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <HiOutlineOfficeBuilding className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <h3 className="text-base font-semibold text-gray-900 truncate leading-tight">
                    {marker.name}
                  </h3>
                  <p className="text-sm text-gray-500 truncate mt-0.5 flex items-center gap-1">
                    <HiOutlineLocationMarker className="w-3.5 h-3.5 flex-shrink-0" />
                    {marker.address || 'Chưa có địa chỉ'}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    {marker.area_m2 && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {marketMapService.formatArea(marker.area_m2)}
                      </span>
                    )}
                    <span className="text-sm font-bold text-emerald-600">
                      {marker.price_text || marketMapService.formatPrice(marker.price_value)}
                    </span>
                  </div>
                </div>

                {/* Action */}
                <div className="flex items-center">
                  {onViewDetail ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewDetail(marker)
                      }}
                      className={clsx(
                        'p-3 rounded-xl transition-all',
                        'bg-emerald-50 text-emerald-600 hover:bg-emerald-100',
                        'active:scale-95'
                      )}
                    >
                      <HiOutlineEye className="w-5 h-5" />
                    </button>
                  ) : (
                    <HiOutlineChevronRight className="w-5 h-5 text-gray-300" />
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <HiOutlineLocationMarker className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Không tìm thấy kết quả
            </h3>
            <p className="text-sm text-gray-500 text-center">
              {searchQuery
                ? `Không có địa điểm nào khớp với "${searchQuery}"`
                : 'Chưa có dữ liệu địa điểm'}
            </p>
            {(searchQuery || activeFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setActiveFilter('all')
                }}
                className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium"
              >
                Xóa bộ lọc
              </button>
            )}
          </div>
        )}
      </div>

      {/* Map info footer */}
      {mapInfo && (
        <div className="p-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">Đang xem</div>
              <div className="text-sm font-medium text-gray-900">{mapInfo.name}</div>
            </div>
            {mapInfo.description && (
              <span className="text-xs text-gray-400 truncate max-w-[150px]">
                {mapInfo.description}
              </span>
            )}
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}

export default MobileMarkerList
